import React, { useEffect } from "react";
import PropTypes from "prop-types";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import PersonIcon from "@mui/icons-material/Person";
import { useForm, Controller } from "react-hook-form";
import PhoneInput, { isPossiblePhoneNumber } from "react-phone-number-input";
import {
    useUpdateUserMutation,
    useCreateUserMutation,
} from "../../../redux/service/modules/admin";
import { useNotification } from "../../../context/NotificationContext";
import { FormField } from "../../../components/common/FormField";
import { ActionButtons } from "../../../components/common/ActionButton";
import CommonDialog from "../../../components/common/CommonDialog";
import "react-phone-number-input/style.css";
import "./PhoneInput.scss";

const UserDetailDialog = ({
    open,
    onClose,
    rowData,
    isEdit,
    title,
    ctaText,
}) => {
    const { control, handleSubmit, setValue, watch, trigger, reset, setError } =
        useForm({
            defaultValues: {
                is_staff: false,
            },
        });
    const [updateUser, { isLoading }] = useUpdateUserMutation();
    const [createUser, { isCreating }] = useCreateUserMutation();

    const profilePicture = watch("profile_picture");
    const { showNotification } = useNotification();

    useEffect(() => {
        if (rowData) {
            Object.keys(rowData).forEach((key) => {
                if (
                    key === "phone_number" &&
                    rowData[key] === "null" &&
                    rowData[key] === "undefined"
                ) {
                    setValue(key, "");
                } else {
                    setValue(key, rowData[key]);
                }
            });
        }
    }, [rowData, setValue]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async (data) => {
        try {
            const formDataToSend = new FormData();
            Object.keys(data).forEach((key) => {
                if (
                    key !== "profile_picture" ||
                    data["profile_picture"] instanceof File
                ) {
                    formDataToSend.append(key, data[key]);
                }
            });
            if (formDataToSend.get("phone_number") === "undefined") {
                formDataToSend.append("phone_number", null);
            }
            if (isEdit) {
                await updateUser({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "User Updated successfully!",
                                ""
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Update User"
                            );
                        }
                    });
            } else {
                formDataToSend.append("password", "Ambit@123");
                await createUser({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "User Created successfully!",
                                ""
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Create User"
                            );
                            handleClose();
                        }
                    })
                    .catch((error) => {
                        if (error.status === 400) {
                            setError("email", {
                                type: "manual",
                                message: error.data.email[0],
                            });
                        }
                    });
            }
        } catch (error) {
            console.error("Failed to update user:", error);
        }
    };

    return (
        <CommonDialog open={open} onClose={handleClose} title={title}>
            <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
                <div className="xl:w-10 2xl:w-15 3xl:w-20 xl:h-10 2xl:h-15 3xl:h-20 rounded-full bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center">
                    {profilePicture instanceof File ? (
                        <img
                            src={URL.createObjectURL(profilePicture)}
                            alt="Profile"
                            className="w-full h-full object-cover"
                        />
                    ) : rowData?.profile_picture_url ? (
                        <img
                            src={rowData.profile_picture_url}
                            alt="Profile"
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <PersonIcon className="xl:text-2xl 2xl:text-3xl 3xl:text-4xl text-gray-400" />
                    )}
                </div>
                <label className="inline-flex xl:text-xs 2xl:text-sm 3xl:text-base items-center gap-2 px-4 py-2 border border-red-500 rounded-md text-primary font-semibold cursor-pointer hover:bg-red-50 transition-colors">
                    <AddAPhotoIcon style={{ fontSize: "1rem" }} />
                    UPLOAD PHOTO
                    <input
                        type="file"
                        accept=".jpeg, .jpg, .png"
                        className="hidden"
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                                setValue("profile_picture", file);
                            }
                        }}
                    />
                </label>
            </div>

            <Controller
                name="username"
                control={control}
                rules={{ required: "Username is required" }}
                render={({ field, fieldState }) => (
                    <div className="space-y-1">
                        <FormField label="User Name" {...field} required />
                        {fieldState.error && (
                            <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-red-500">
                                {fieldState.error.message}
                            </span>
                        )}
                    </div>
                )}
            />

            <Controller
                name="email"
                control={control}
                rules={{
                    required: "Email is required",
                    pattern: {
                        value: /^(?!\d+$)[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Invalid email address",
                    },
                }}
                render={({ field, fieldState }) => (
                    <div className="space-y-1">
                        <FormField
                            label="Email"
                            {...field}
                            disabled={isEdit}
                            required
                        />
                        {fieldState.error && (
                            <span className="xl:text-xs 2xl:text-sm 3xl:text-base text-red-500">
                                {fieldState.error.message}
                            </span>
                        )}
                    </div>
                )}
            />

            <div className="space-y-2">
                <label className="block xl:text-xs 2xl:text-sm 3xl:text-base font-medium text-gray-700">
                    Role
                </label>
                <Controller
                    name="role"
                    control={control}
                    rules={{ required: "Role is required" }}
                    render={({ field, fieldState }) => (
                        <>
                            <select
                                {...field}
                                onChange={(e) => field.onChange(e.target.value)}
                                className="w-full xl:px-2 2xl:px-3 3xl:px-4 xl:py-1.5 2xl:py-2 3xl:py-2.5 xl:text-xs 2xl:text-sm 3xl:text-base
                                xl:border xl:rounded-md 2xl:border-2 2xl:rounded-lg 3xl:border-2 3xl:rounded-lg
                                border-gray-200 focus:outline-none xl:focus:ring-1 2xl:focus:ring-2 3xl:focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                            >
                                <option value="admin">Admin</option>
                                <option value="analyst">Analyst</option>
                                <option value="head_of_sales">
                                    Head of Sales
                                </option>
                                <option value="head_of_research">
                                    Head of Research
                                </option>
                                <option value="sales">Sales</option>
                                <option value="hybrid">Hybrid</option>
                                <option value="trader">Trader</option>
                            </select>
                            {fieldState.error && (
                                <span className="block xl:text-xs 2xl:text-sm 3xl:text-base text-red-500 mt-1">
                                    {fieldState.error.message}
                                </span>
                            )}
                        </>
                    )}
                />
            </div>

            <div className="space-y-2">
                <label className="block xl:text-xs 2xl:text-sm 3xl:text-base font-medium text-gray-700">
                    Contact Number
                </label>
                <Controller
                    name="phone_number"
                    control={control}
                    rules={{
                        validate: {
                            possible: (value) => {
                                if (value === "null" || value === "undefined") {
                                    return true;
                                }
                                return (
                                    !value ||
                                    isPossiblePhoneNumber(value) ||
                                    "Invalid phone number"
                                );
                            },
                            length: (value) => {
                                if (value === "null" || value === "undefined") {
                                    return true;
                                }
                                const digitsOnly = value
                                    ? value.replace(/\D/g, "")
                                    : "";
                                if (digitsOnly.startsWith("91")) {
                                    return (
                                        !value ||
                                        digitsOnly.length === 12 ||
                                        "Phone number must be 10 digits for IN"
                                    );
                                }
                                return (
                                    !value ||
                                    digitsOnly.length >= 10 ||
                                    "Phone number is too short"
                                );
                            },
                        },
                    }}
                    render={({ field, fieldState }) => (
                        <>
                            <PhoneInput
                                {...field}
                                defaultCountry="IN"
                                onChange={(value) => {
                                    field.onChange(value);
                                    trigger("phone_number");
                                }}
                                className="w-full xl:px-2 2xl:px-3 3xl:px-4 xl:py-1.5 2xl:py-2 3xl:py-2.5 xl:text-xs 2xl:text-sm 3xl:text-base
                                xl:border xl:rounded-md 2xl:border-2 2xl:rounded-lg 3xl:border-2 3xl:rounded-lg
                                border-gray-200 focus:outline-none xl:focus:ring-1 2xl:focus:ring-2 3xl:focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                            />
                            {fieldState.error && (
                                <span className="block xl:text-xs 2xl:text-sm 3xl:text-base text-red-500 mt-1">
                                    {fieldState.error.message}
                                </span>
                            )}
                        </>
                    )}
                />
            </div>

            <ActionButtons
                onCancel={handleClose}
                ctaText={ctaText}
                onSave={handleSubmit(onSubmit)}
                isLoading={isLoading || isCreating}
            />
        </CommonDialog>
    );
};

UserDetailDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    rowData: PropTypes.object,
    isEdit: PropTypes.bool,
    title: PropTypes.string,
    ctaText: PropTypes.string,
};

export default UserDetailDialog;
